import React, {useEffect, useState} from 'react';
import {getCssVarValue} from '@src/helpers/getCssVarValue';
import {MomentJs, parseDate} from '@src/helpers/dates';
import StatusColorListIndicator from './fields/StatusColorListIndicator';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {HexColor} from '@src/@types/colors';
import {DateTime} from '@src/@types/DateTime';
import {getProductWithNearestDeadline} from './OrderListDeadlineAtColumn';
import {notNull} from '@src/helpers/ts/notNull';

export default function OrderListStatusIndicator<Model extends IOrderModel & {
    status_bg_color?: HexColor,
    status_will_be_expired_at?: DateTime | null,
}>(
        {model}: { model: Model | undefined, rowIndex: number }
) {
    const [, setLastRenderTime] = useState(Date.now());
    const nearestProductDeadline = getProductWithNearestDeadline(
            model?.products,
            product => (
                    !product.attention &&
                    (
                            !product.status?.is_completed ||
                            product.status?.approval_is_required === true &&
                            product.status_approval_state !== 'accepted'
                    )
            )
    )?.deadline_at;

    const earliestDeadline = [
        (model?.status?.is_completed || model?.status?.is_completed_without_payment) ? null : model?.deadline_at,
        model?.status_requires_reason_of_expiration ? model?.status_will_be_expired_at : null,
        nearestProductDeadline,
    ].filter(notNull).map<MomentJs>(date => {
        if (typeof date === 'string') {
            return parseDate(date);
        }

        return date;
    }).sort((a, b) => {
        return +a - +b;
    }).at(0);

    const isCompleted = (
            model?.status?.is_completed === true ||
            model?.status?.is_completed_without_payment === true
    );
    const isExpired = !isCompleted && earliestDeadline != null && +earliestDeadline <= Date.now();

    useEffect(() => {
        if (isExpired || !earliestDeadline) {
            return;
        }

        const timer = setTimeout(() => {
            setLastRenderTime(Date.now());
        }, +earliestDeadline - Date.now());

        return () => clearTimeout(timer);
    }, [isExpired, earliestDeadline]);

    let color = model?.status_bg_color;
    if (isExpired) {
        color = getCssVarValue('--bs-danger', '#f00');
    }

    return <td className="p-0">
        <StatusColorListIndicator
                color={color}
                margin={'5px 0 5px 5px'}
                pulse={isExpired}
        />
    </td>;
}
